import { useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { zipCodeRegex } from 'common-utils';
import { styled } from 'styled-components';

import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import partition from 'lodash/partition';
import sortBy from 'lodash/sortBy';

import { t } from 'translations';
import { DropDown, DropdownOption, IconButton, Select, SelectItem, TextField } from 'ui';
import { StyledMinusIcon, StyledPlusIcon } from 'ui/components/Icons';
import { Tooltip } from 'ui/components/Tooltip';
import { StyledClickToShowInputButton } from 'ui/styles/forms';
import { Paragraph, SmallText } from 'ui/styles/text';
import { media } from 'ui/themes/signup';

import type { CountryWeSellTo } from '../../types';
import type { FormValues } from './types';

import { streetAddressContainsRestrictedCharacters } from '../../utils/address';
import { Flags, useFlag } from '../../utils/featureFlags';
import { removeSpaces } from '../../utils/helpers';

const FlexWrap = styled.div`
  display: flex;
  flex-direction: column;
  ${media.medium`
    flex-direction: row;
  `}
  gap: 10px;
`;

interface AddressInputsProps {
  addressType: 'billingAddress' | 'shippingAddress';
  countries: CountryWeSellTo[];
  regions: CountryWeSellTo['regions'];
  allowCountryChange: boolean;
  addFocusToHomeAddress: boolean;
}

export const AddressInputs = ({
  addressType,
  countries,
  regions,
  allowCountryChange,
  addFocusToHomeAddress,
}: AddressInputsProps) => {
  const intl = useIntl();
  const newCountrySelectorFlag = useFlag(Flags.NEW_COUNTRY_SELECTOR);

  const allCountryOptions: DropdownOption[] = countries.map(({ countryCode, name }) => ({
    value: countryCode,
    label: intl.formatMessage({ id: `country_${countryCode}` }) ?? name,
  }));
  // Put the country that matches the locale to the top of the list
  const [suggested, rest] = partition(allCountryOptions, ({ value }) =>
    // Try to match the country based on the locale (e.g. en-US -> US, fi -> FI)
    value.includes(intl.locale.slice(-2).toUpperCase()),
  );

  const countryOptions = suggested.concat(sortBy(rest, 'label'));

  const {
    control,
    formState: { errors },
    getValues,
    register,
    resetField,
    setValue,
    unregister,
    setFocus,
  } = useFormContext<FormValues>();

  useEffect(() => {
    if (addFocusToHomeAddress) {
      setFocus('shippingAddress.firstName');
    }
  }, [addFocusToHomeAddress, setFocus]);

  const showSecondAddressField = !isNil(getValues(`${addressType}.address2`));
  const openSecondAddressField = () => {
    setValue(`${addressType}.address2`, '');
    requestAnimationFrame(() => setFocus(`${addressType}.address2`));
  };
  const displayState = !isEmpty(regions);

  const lokalisedStateList: DropdownOption[] = sortBy(
    regions.map(({ code, name }) => {
      const noSpaceStateCode = removeSpaces(code);
      return {
        value: code,
        label:
          intl.formatMessage({
            id: `state_${getValues(`${addressType}.country`)}_${noSpaceStateCode}`,
          }) ?? name,
      };
    }),
    'label',
  );

  const countryDropdownProps = {
    id: `${addressType}.country`,
    'data-testid': `${addressType}.country`,
    label: allowCountryChange
      ? intl.formatMessage({
          id: 'membership_signup_billing_address_country_field',
        })
      : intl.formatMessage({
          id: 'membership_address_country_disabled_field',
        }),

    'aria-describedby': allowCountryChange ? undefined : 'country-disabled-tooltip',
    autoComplete: 'country',
  };

  const countryDropdown = !newCountrySelectorFlag ? (
    <DropDown
      {...countryDropdownProps}
      {...register(`${addressType}.country`, {
        onChange: () => {
          resetField(`${addressType}.state`);
        },
        required: {
          value: allowCountryChange,
          message: intl.formatMessage({
            id: 'membership_addressform_error_country_missing',
          }),
        },
      })}
      firstOptionEmpty
      value={getValues(`${addressType}.country`)}
      options={countryOptions}
      error={errors[addressType]?.country?.message}
      disabled={!allowCountryChange}
    />
  ) : (
    <Controller
      control={control}
      name={`${addressType}.country`}
      rules={{
        required: {
          value: allowCountryChange,
          message: intl.formatMessage({
            id: 'membership_addressform_error_country_missing',
          }),
        },
      }}
      render={({ field: { name, value, onChange, onBlur }, fieldState: { invalid, error } }) => (
        <Select
          name={name}
          search={{
            placeholder: 'Search countries',
            emptyText: 'No results found',
          }}
          items={countryOptions.map(({ value, label }) => ({ id: value, label }))}
          selectedKey={value}
          isDisabled={!allowCountryChange}
          isInvalid={invalid}
          error={error?.message}
          {...countryDropdownProps}
          onBlur={onBlur}
          onSelectionChange={(key) => {
            onChange(key);
            resetField(`${addressType}.state`);
          }}
        >
          {({ label }) => <SelectItem>{label}</SelectItem>}
        </Select>
      )}
    />
  );

  const countrySelector = allowCountryChange ? (
    countryDropdown
  ) : (
    <Tooltip
      id="country-disabled-tooltip"
      tipContent={
        <Paragraph data-testid="tooltip-content" $fontSize="small">
          {t('membership_hub_address_country_field_disabled_reason')}
        </Paragraph>
      }
    >
      {countryDropdown}
    </Tooltip>
  );

  return (
    <>
      <TextField
        id={`${addressType}.firstName`}
        required={true}
        placeholder={intl.formatMessage({
          id: 'membership_signup_billing_address_first_name_field',
        })}
        placeholderAsLabel
        aria-describedby={`${addressType}.firstName-hint`}
        autoComplete="given-name"
        error={errors[addressType]?.firstName?.message}
        {...register(`${addressType}.firstName`, {
          required: intl.formatMessage({
            id: 'membership_addressform_error_firstname_missing',
          }),
          maxLength: {
            value: 100,
            message: intl.formatMessage({
              id: 'membership_addressform_error_too_long',
            }),
          },
        })}
      />
      <TextField
        id={`${addressType}.lastName`}
        required={true}
        placeholder={intl.formatMessage({
          id: 'membership_signup_billing_address_last_name_field',
        })}
        placeholderAsLabel
        aria-describedby={`${addressType}.lastName-hint`}
        autoComplete="family-name"
        error={errors[addressType]?.lastName?.message}
        {...register(`${addressType}.lastName`, {
          required: intl.formatMessage({
            id: 'membership_addressform_error_lastname_missing',
          }),
          maxLength: {
            value: 100,
            message: intl.formatMessage({
              id: 'membership_addressform_error_too_long',
            }),
          },
        })}
      />
      <TextField
        id={`${addressType}.address1`}
        required={true}
        placeholder={intl.formatMessage({
          id: 'membership_signup_billing_address_address_field',
        })}
        placeholderAsLabel
        aria-describedby={`${addressType}.address1-hint`}
        autoComplete="address-line1"
        error={errors[addressType]?.address1?.message}
        {...register(`${addressType}.address1`, {
          required: intl.formatMessage({
            id: 'membership_addressform_error_address1_missing',
          }),
          maxLength: {
            value: 255,
            message: intl.formatMessage({
              id: 'membership_addressform_error_too_long',
            }),
          },
          validate: (value) =>
            !streetAddressContainsRestrictedCharacters(value) ||
            intl.formatMessage({
              id: 'membership_address_contains_restricted_characters',
            }),
        })}
      />

      {showSecondAddressField && (
        <TextField
          id={`${addressType}.address2`}
          placeholder={intl.formatMessage({
            id: 'membership_signup_billing_address_address2_field',
          })}
          placeholderAsLabel
          autoComplete="address-line2"
          error={errors[addressType]?.address2?.message}
          {...register(`${addressType}.address2`, {
            maxLength: {
              value: 255,
              message: intl.formatMessage({
                id: 'membership_addressform_error_too_long',
              }),
            },
          })}
        >
          <IconButton
            name={intl.formatMessage(
              {
                id: 'membereship-close-address2-button',
              },
              { addressType },
            )}
            type="button"
            onClick={() => unregister(`${addressType}.address2`)}
            icon={<StyledMinusIcon aria-hidden="true" focusable="false" />}
          />
        </TextField>
      )}
      {!showSecondAddressField && (
        <StyledClickToShowInputButton
          type="button"
          onClick={openSecondAddressField}
          aria-expanded={showSecondAddressField}
          aria-controls={`${addressType}.address2`}
        >
          <StyledPlusIcon aria-hidden="true" focusable="false" />
          <SmallText style={{ paddingLeft: '10px', margin: 0 }}>
            {t('membership_signup_billing_address_billing_apt')}
          </SmallText>
        </StyledClickToShowInputButton>
      )}

      <FlexWrap>
        <TextField
          id={`${addressType}.city`}
          required={true}
          placeholder={intl.formatMessage({
            id: 'membership_signup_billing_address_city_field',
          })}
          placeholderAsLabel
          aria-describedby={`${addressType}.city-hint`}
          autoComplete="address-level2"
          error={errors[addressType]?.city?.message}
          {...register(`${addressType}.city`, {
            required: intl.formatMessage({
              id: 'membership_addressform_error_city_missing',
            }),
            maxLength: {
              value: 40,
              message: intl.formatMessage({
                id: 'membership_addressform_error_too_long',
              }),
            },
          })}
        />
        {displayState && (
          <DropDown
            id={`${addressType}.state`}
            required={true}
            label={intl.formatMessage({
              id: 'membership_signup_billing_address_billing_state',
            })}
            firstOptionEmpty
            aria-describedby={`${addressType}.state-hint`}
            autoComplete="address-level1"
            value={getValues(`${addressType}.state`)}
            options={lokalisedStateList}
            error={errors[addressType]?.state?.message}
            {...register(`${addressType}.state`, {
              required: intl.formatMessage({
                id: 'membership_addressform_error_state_missing',
              }),
              shouldUnregister: true,
            })}
          />
        )}
      </FlexWrap>

      <FlexWrap>
        {countrySelector}

        <TextField
          id={`${addressType}.postalCode`}
          required={true}
          placeholder={intl.formatMessage({
            id: 'membership_signup_billing_address_billing_zip',
          })}
          placeholderAsLabel
          aria-describedby={`${addressType}.postalCode-hint`}
          autoComplete="postal-code"
          error={errors[addressType]?.postalCode?.message}
          {...register(`${addressType}.postalCode`, {
            required: {
              value: true,
              message: intl.formatMessage({
                id: 'membership_addressform_error_zipcode_missing',
              }),
            },
            maxLength: {
              value: 10,
              message: intl.formatMessage({
                id: 'membership_addressform_error_too_long',
              }),
            },
            pattern: {
              value: zipCodeRegex,
              message: intl.formatMessage({
                id: 'membership_addressform_error_zipcode_invalid',
              }),
            },
          })}
        />
      </FlexWrap>
    </>
  );
};
